import React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, Navigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingSettings } from "@/hooks/useOnboardingSettings";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { StepAdProfiles } from "@/components/onboarding/steps/StepAdProfiles";
import { StepTracking } from "@/components/onboarding/steps/StepTracking";
import { StepNamingConvention } from "@/components/onboarding/steps/StepNamingConvention";
import { StepLaunchSettings } from "@/components/onboarding/steps/StepLaunchSettings";
import { StepEnhancements } from "@/components/onboarding/steps/StepEnhancements";

interface ConfigureAccount {
  id: string;
  ad_account_id: string;
  ad_account_name: string;
}

const STEPS = [
  { label: "Ad Profiles", key: "step_profiles_completed" },
  { label: "Tracking", key: "step_tracking_completed" },
  { label: "Naming", key: "step_naming_completed" },
  { label: "Launch", key: "step_launch_completed" },
  { label: "Enhancements", key: "step_enhancements_completed" },
] as const;

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepValid, setStepValid] = useState(false);

  // Read "configure first" account from localStorage
  const account = useMemo<ConfigureAccount | null>(() => {
    try {
      const raw = localStorage.getItem("tbd-configure-first-account");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.ad_account_id) return parsed as ConfigureAccount;
      return null;
    } catch {
      return null;
    }
  }, []);

  const { settings, isLoading, saveStep, isSaving } = useOnboardingSettings(account?.ad_account_id);

  // Fire-and-forget asset sync via n8n-proxy (SetupLayout doesn't include useBackgroundSync)
  useEffect(() => {
    if (!user?.id || !account?.ad_account_id) return;
    const payload = { ad_account_id: account.ad_account_id };
    supabase.functions.invoke("n8n-proxy", { body: { action: "GET_ASSETS", payload } }).catch(() => {});
    supabase.functions.invoke("n8n-proxy", { body: { action: "GET_CAMPAIGNS", payload } }).catch(() => {});
    supabase.functions.invoke("n8n-proxy", { body: { action: "GET_PIXELS", payload } }).catch(() => {});
    supabase.functions.invoke("n8n-proxy", { body: { action: "GET_APPS", payload } }).catch(() => {});
  }, [user?.id, account?.ad_account_id]);

  // Resume from last completed step — only on initial mount
  const hasResumed = useRef(false);
  useEffect(() => {
    if (!settings || hasResumed.current) return;
    hasResumed.current = true;
    if (settings.step_enhancements_completed) setCurrentStep(5);
    else if (settings.step_launch_completed) setCurrentStep(5);
    else if (settings.step_naming_completed) setCurrentStep(4);
    else if (settings.step_tracking_completed) setCurrentStep(3);
    else if (settings.step_profiles_completed) setCurrentStep(2);
  }, [settings]);

  if (!account) {
    return <Navigate to="/setup/select-accounts" replace />;
  }

  const isLastStep = currentStep === 5;

  const handleNext = async () => {
    if (isLastStep) {
      await handleFinishSetup();
    } else {
      setCurrentStep((s) => s + 1);
      setStepValid(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
      setStepValid(true); // previous step was already completed
    }
  };

  const handleFinishSetup = async () => {
    if (!user?.id) return;
    // Mark onboarding completed
    await supabase
      .from("ad_account_settings")
      .update({
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
      })
      .eq("user_id", user.id)
      .eq("ad_account_id", account.ad_account_id);

    // Update setup_status on ad_accounts
    await supabase
      .from("ad_accounts")
      .update({ setup_status: "completed" as any })
      .eq("user_id", user.id)
      .eq("ad_account_id", account.ad_account_id);

    // Update onboarding_status
    await supabase
      .from("users")
      .update({ onboarding_status: "COMPLETED" } as any)
      .eq("id", user.id);

    queryClient.invalidateQueries({ queryKey: ["ad-account-list"] });
    queryClient.invalidateQueries({ queryKey: ["ad-account-statuses"] });
    queryClient.invalidateQueries({ queryKey: ["ad-account-row"] });

    localStorage.removeItem("tbd-configure-first-account");
    navigate("/creatives/library", { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Account badge */}
      <div className="text-center">
        <Badge variant="secondary" className="text-xs font-normal">
          Setting up: {account.ad_account_name}
        </Badge>
      </div>

      {/* Stepper */}
      <nav className="flex items-center justify-between gap-1">
        {STEPS.map((step, i) => {
          const stepNum = i + 1;
          const isCompleted = settings?.[step.key as keyof typeof settings] === true;
          const isCurrent = stepNum === currentStep;

          return (
            <div key={step.key} className="flex flex-1 flex-col items-center gap-1.5">
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                  isCompleted
                    ? "bg-primary text-primary-foreground"
                    : isCurrent
                      ? "border-2 border-primary text-primary bg-background"
                      : "border border-border text-muted-foreground bg-background",
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : stepNum}
              </div>
              <span
                className={cn(
                  "text-[11px] leading-tight text-center",
                  isCurrent ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </nav>

      {/* Step content */}
      <div className="min-h-[280px]">
        {currentStep === 1 && (
          <StepAdProfiles
            adAccountId={account.ad_account_id}
            settings={settings}
            onSave={saveStep}
            onValidChange={setStepValid}
          />
        )}
        {currentStep === 2 && (
          <StepTracking
            adAccountId={account.ad_account_id}
            settings={settings}
            onSave={saveStep}
            onValidChange={setStepValid}
          />
        )}
        {currentStep === 3 && (
          <StepNamingConvention
            adAccountId={account.ad_account_id}
            settings={settings}
            onSave={saveStep}
            onValidChange={setStepValid}
          />
        )}
        {currentStep === 4 && (
          <StepLaunchSettings
            adAccountId={account.ad_account_id}
            settings={settings}
            onSave={saveStep}
            onValidChange={setStepValid}
          />
        )}
        {currentStep === 5 && (
          <StepEnhancements
            adAccountId={account.ad_account_id}
            settings={settings}
            onSave={saveStep}
            onValidChange={setStepValid}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          onClick={handleBack}
          disabled={currentStep === 1}
          className={currentStep === 1 ? "invisible" : ""}
        >
          Back
        </Button>
        <Button onClick={handleNext} disabled={!stepValid || isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
}
